import React, { useState } from 'react';
import { useCategories } from '../hooks/useCategories';
import './CategoryManagement.css';

function CategoryManagement() {
  const { categories, loading, updateCategories, fetchCategories } = useCategories();
  const [editingCategory, setEditingCategory] = useState(null);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const [newCategory, setNewCategory] = useState({
    name: '',
    budget_group: 'wants',
    subcategories: ['']
  });

  const handleChangeBudgetGroup = async (categoryName, newGroup) => {
    const updatedCategories = { ...categories };
    updatedCategories[categoryName] = {
      ...updatedCategories[categoryName],
      budget_group: newGroup
    };

    setSaving(true);
    setError(null);
    const result = await updateCategories(updatedCategories);
    setSaving(false);

    if (result.success) {
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } else {
      setError(result.error);
    }
  };

  const handleAddSubcategory = (categoryName, subcategory) => {
    if (!subcategory.trim()) return;

    const updatedCategories = { ...categories };
    if (!updatedCategories[categoryName].subcategories.includes(subcategory)) {
      updatedCategories[categoryName].subcategories.push(subcategory);
      saveCategories(updatedCategories);
    }
  };

  const handleRemoveSubcategory = (categoryName, subcategory) => {
    const updatedCategories = { ...categories };
    updatedCategories[categoryName].subcategories = 
      updatedCategories[categoryName].subcategories.filter(s => s !== subcategory);
    saveCategories(updatedCategories);
  };

  const handleAddNewCategory = async () => {
    if (!newCategory.name.trim()) {
      setError('Category name is required');
      return;
    }

    if (categories[newCategory.name]) {
      setError('Category already exists');
      return;
    }

    const updatedCategories = {
      ...categories,
      [newCategory.name]: {
        budget_group: newCategory.budget_group,
        subcategories: newCategory.subcategories.filter(s => s.trim() !== '')
      }
    };

    const result = await saveCategories(updatedCategories);
    if (result.success) {
      setShowAddCategory(false);
      setNewCategory({ name: '', budget_group: 'wants', subcategories: [''] });
    }
  };

  const handleDeleteCategory = async (categoryName) => {
    if (!window.confirm(`Delete category "${categoryName}"? This cannot be undone.`)) {
      return;
    }

    const updatedCategories = { ...categories };
    delete updatedCategories[categoryName];
    saveCategories(updatedCategories);
  };

  const saveCategories = async (updatedCategories) => {
    setSaving(true);
    setError(null);
    const result = await updateCategories(updatedCategories);
    setSaving(false);

    if (result.success) {
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
      await fetchCategories();
    } else {
      setError(result.error);
    }

    return result;
  };

  const getCategoriesByGroup = (group) => {
    return Object.entries(categories)
      .filter(([_, data]) => data.budget_group === group)
      .sort(([a], [b]) => a.localeCompare(b));
  };

  if (loading) {
    return (
      <div className="category-management">
        <div className="loading">Loading categories...</div>
      </div>
    );
  }

  return (
    <div className="category-management">
      <div className="category-header">
        <h2>📂 Category & Budget Group Management</h2>
        <button className="add-category-btn" onClick={() => setShowAddCategory(true)}>
          + Add Category
        </button>
      </div>

      {success && (
        <div className="success-message">
          ✓ Categories saved successfully!
        </div>
      )}

      {error && (
        <div className="error-message">
          ⚠️ {error}
        </div>
      )}

      <p className="category-description">
        Organize your expense categories into budget groups. This affects how the Budget Tracker 
        calculates your spending against Needs, Wants, and Savings.
      </p>

      {/* Needs Categories */}
      <div className="budget-group-section needs">
        <h3>🏠 Needs (Essential Expenses)</h3>
        <div className="categories-list">
          {getCategoriesByGroup('needs').map(([name, data]) => (
            <CategoryCard
              key={name}
              name={name}
              data={data}
              onChangeBudgetGroup={handleChangeBudgetGroup}
              onAddSubcategory={handleAddSubcategory}
              onRemoveSubcategory={handleRemoveSubcategory}
              onDelete={handleDeleteCategory}
              editingCategory={editingCategory}
              setEditingCategory={setEditingCategory}
            />
          ))}
          {getCategoriesByGroup('needs').length === 0 && (
            <p className="empty-group">No categories in Needs</p>
          )}
        </div>
      </div>

      {/* Wants Categories */}
      <div className="budget-group-section wants">
        <h3>🎉 Wants (Discretionary Spending)</h3>
        <div className="categories-list">
          {getCategoriesByGroup('wants').map(([name, data]) => (
            <CategoryCard
              key={name}
              name={name}
              data={data}
              onChangeBudgetGroup={handleChangeBudgetGroup}
              onAddSubcategory={handleAddSubcategory}
              onRemoveSubcategory={handleRemoveSubcategory}
              onDelete={handleDeleteCategory}
              editingCategory={editingCategory}
              setEditingCategory={setEditingCategory}
            />
          ))}
          {getCategoriesByGroup('wants').length === 0 && (
            <p className="empty-group">No categories in Wants</p>
          )}
        </div>
      </div>

      {/* Savings Categories */}
      <div className="budget-group-section savings">
        <h3>💰 Savings (Savings & Investments)</h3>
        <div className="categories-list">
          {getCategoriesByGroup('savings').map(([name, data]) => (
            <CategoryCard
              key={name}
              name={name}
              data={data}
              onChangeBudgetGroup={handleChangeBudgetGroup}
              onAddSubcategory={handleAddSubcategory}
              onRemoveSubcategory={handleRemoveSubcategory}
              onDelete={handleDeleteCategory}
              editingCategory={editingCategory}
              setEditingCategory={setEditingCategory}
            />
          ))}
          {getCategoriesByGroup('savings').length === 0 && (
            <p className="empty-group">No categories in Savings</p>
          )}
        </div>
      </div>

      {/* Add Category Modal */}
      {showAddCategory && (
        <div className="modal-overlay" onClick={() => setShowAddCategory(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Add New Category</h3>
              <button className="close-btn" onClick={() => setShowAddCategory(false)}>✕</button>
            </div>

            <div className="modal-body">
              <div className="form-group">
                <label>Category Name *</label>
                <input
                  type="text"
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  placeholder="e.g., Pets, Hobbies"
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label>Budget Group *</label>
                <select
                  value={newCategory.budget_group}
                  onChange={(e) => setNewCategory({ ...newCategory, budget_group: e.target.value })}
                >
                  <option value="needs">🏠 Needs (Essential)</option>
                  <option value="wants">🎉 Wants (Discretionary)</option>
                  <option value="savings">💰 Savings & Investments</option>
                </select>
              </div>

              <div className="form-group">
                <label>Subcategories (Optional)</label>
                {newCategory.subcategories.map((sub, index) => (
                  <div key={index} className="subcategory-input">
                    <input
                      type="text"
                      value={sub}
                      onChange={(e) => {
                        const updated = [...newCategory.subcategories];
                        updated[index] = e.target.value;
                        setNewCategory({ ...newCategory, subcategories: updated });
                      }}
                      placeholder="e.g., Dog Food, Cat Toys"
                    />
                    {index === newCategory.subcategories.length - 1 && (
                      <button
                        type="button"
                        className="add-sub-btn"
                        onClick={() => setNewCategory({
                          ...newCategory,
                          subcategories: [...newCategory.subcategories, '']
                        })}
                      >
                        +
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="modal-actions">
              <button className="cancel-btn" onClick={() => setShowAddCategory(false)}>
                Cancel
              </button>
              <button className="save-btn" onClick={handleAddNewCategory} disabled={saving}>
                {saving ? 'Adding...' : 'Add Category'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Category Card Component
function CategoryCard({ 
  name, 
  data, 
  onChangeBudgetGroup, 
  onAddSubcategory, 
  onRemoveSubcategory,
  onDelete,
  editingCategory,
  setEditingCategory
}) {
  const [newSubcategory, setNewSubcategory] = useState('');
  const [showSubcategories, setShowSubcategories] = useState(false);

  const handleAddSubcategory = () => {
    if (newSubcategory.trim()) {
      onAddSubcategory(name, newSubcategory.trim());
      setNewSubcategory('');
    }
  };

  return (
    <div className="category-card">
      <div className="category-main">
        <div className="category-info">
          <h4>{name}</h4>
          <span className="subcategory-count">
            {data.subcategories.length} subcategories
          </span>
        </div>

        <div className="category-actions">
          <select
            className="budget-group-select"
            value={data.budget_group}
            onChange={(e) => onChangeBudgetGroup(name, e.target.value)}
          >
            <option value="needs">🏠 Needs</option>
            <option value="wants">🎉 Wants</option>
            <option value="savings">💰 Savings</option>
          </select>

          <button
            className="toggle-btn"
            onClick={() => setShowSubcategories(!showSubcategories)}
            title="Show subcategories"
          >
            {showSubcategories ? '▲' : '▼'}
          </button>

          <button
            className="delete-btn"
            onClick={() => onDelete(name)}
            title="Delete category"
          >
            🗑️
          </button>
        </div>
      </div>

      {showSubcategories && (
        <div className="subcategories-section">
          <div className="subcategories-list">
            {data.subcategories.map((sub, index) => (
              <div key={index} className="subcategory-item">
                <span>{sub}</span>
                <button
                  className="remove-sub-btn"
                  onClick={() => onRemoveSubcategory(name, sub)}
                  title="Remove subcategory"
                >
                  ✕
                </button>
              </div>
            ))}
          </div>

          <div className="add-subcategory">
            <input
              type="text"
              value={newSubcategory}
              onChange={(e) => setNewSubcategory(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddSubcategory()}
              placeholder="Add subcategory..."
            />
            <button onClick={handleAddSubcategory}>Add</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default CategoryManagement;
